"use client";

import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocalDueDate } from "@/lib/use-local-due-date";

/**
 * The one due-date line: "Due Sep 13", "Due Sep 13 · Overdue", tinted red
 * when overdue and amber when due within two days. `LocalDueDate` computes
 * the state from a raw date; `DueDateLabel` renders a state the caller
 * already has (AssignmentCard, which decides `overdue` per completion).
 * `detail` is for the overdue report, where items can be months old: the date
 * carries its year and "Overdue" becomes "40 days overdue".
 */
export function DueDateLabel({
  due,
  overdue = Boolean(due?.overdue),
  soon = Boolean(due?.soon),
  detail = false,
  showIcon = false,
  className,
}) {
  if (!due) return null;

  const daysOverdue = detail && overdue && due.daysOverdue > 0 ? due.daysOverdue : 0;

  const color = overdue
    ? "font-medium text-red-600"
    : soon
      ? "font-medium text-warning-600"
      : "text-gray-500";

  return (
    <time dateTime={due.iso} className={cn("inline-flex items-center", color, className)}>
      {/* The text carries the overdue/soon tint; the icon stays neutral. */}
      {showIcon && <Clock className="w-3.5 h-3.5 mr-1.5 text-gray-400" aria-hidden />}
      {/* One inline span for the words: flex would swallow the spaces
          between them, and the spaces have to survive for copy and readers. */}
      <span>
        Due <span>{detail ? due.labelWithYear : due.label}</span>
        {daysOverdue > 0 ? (
          <span className="tabular-nums">
            {" "}· {daysOverdue} {daysOverdue === 1 ? "day" : "days"} overdue
          </span>
        ) : overdue ? (
          " · Overdue"
        ) : null}
      </span>
    </time>
  );
}

export default function LocalDueDate({
  value,
  className = "text-[11.5px]",
  showIcon = false,
}) {
  // The server and hydration render both omit the date. Once hydrated, use
  // the same browser timezone as the home page's Today heading.
  const due = useLocalDueDate(value);
  return <DueDateLabel due={due} showIcon={showIcon} className={className} />;
}
